'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsRocketTakeoffFill } from 'react-icons/bs'
import {
  FaBookOpen,
  FaBullhorn,
  FaChevronDown,
  FaChevronRight,
  FaChevronUp,
  FaCode,
  FaCog,
  FaCrown,
  FaDiscord,
  FaGem,
  FaHome,
  FaLayerGroup,
  FaSearch,
  FaTimes,
  FaUser,
} from 'react-icons/fa'
import { HiMail } from 'react-icons/hi'
import { useAuth } from '../contexts/AuthContext'
import useStaffTools from '../hooks/useStaffTools'
import { searchPath } from '../helpers/searchPath'

const SUBTLE = '#9A9AA8'

// `safe` renders the app-store-safe variant: no search, no monetization
// (gems/upgrade/daily reward), and no off-platform links (Discord/Guide/API).
export default function ProfileDrawer({ open, onClose, safe = false }) {
  const { t } = useTranslation()
  const pathname = usePathname()
  const router = useRouter()
  const {
    user,
    username,
    email,
    displayProfilePicture,
    displayPlan,
    isPlusOrHigher,
  } = useAuth()
  // The categories page (/categories) is staff only for now; the header's button for it shows from md up only.
  const { staff } = useStaffTools(user)

  const [searchInput, setSearchInput] = useState('')
  const [moreOpen, setMoreOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  // Close on route change (covers links we don't decorate, e.g. the gem CTA).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onClose()
  }, [pathname])

  // Account-scoped: the avatar that opens this only renders when signed in.
  if (!user) return null

  const planDisplay =
    displayPlan === 'Pro'
      ? '🔥 Pro'
      : displayPlan === 'Premium'
        ? '🚀 Premium'
        : displayPlan === 'Plus'
          ? '🎉 Plus'
          : 'Free 🌱'

  const planTextClass = displayPlan === 'Pro' ? 'text-[#FFB419]' : 'text-white'

  const submitSearch = () => {
    const q = searchInput.trim()
    if (!q) return
    router.push(searchPath(q))
    onClose()
  }

  const handleSearch = (e) => {
    if (e.key !== 'Enter') return
    submitSearch()
  }

  const Row = ({ icon, label, href, external, active, badge, onClick }) => {
    const className = `group flex items-center gap-4 rounded-xl px-3 py-3 transition-colors hover:bg-[var(--foreground-bg-color)] ${
      active ? 'text-white font-semibold' : 'text-[#9A9AA8]'
    }`
    const inner = (
      <>
        <span className="flex w-5 justify-center text-[17px]">{icon}</span>
        <span className="flex-1 text-[15px]">{label}</span>
        {badge ? (
          <span className="rounded-full bg-[var(--main-bg-color)] px-2 py-0.5 text-xs font-bold text-white">
            {badge}
          </span>
        ) : active ? (
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--main-bg-color)]" />
        ) : null}
      </>
    )
    const close = () => {
      onClick?.()
      onClose()
    }
    return external ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={close}
        className={className}
      >
        {inner}
      </a>
    ) : (
      <Link href={href} onClick={close} className={className}>
        {inner}
      </Link>
    )
  }

  const avatar = displayProfilePicture ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={displayProfilePicture}
      alt={username || 'Profile'}
      className="h-11 w-11 rounded-full object-cover object-top"
    />
  ) : username ? (
    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--main-bg-color)] font-bold uppercase text-white">
      {username.charAt(0)}
    </span>
  ) : (
    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--foreground-bg-color)] text-white">
      <FaUser />
    </span>
  )

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden="true"
        className={`fixed inset-0 z-[90] bg-black/60 transition-opacity duration-200 ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        className={`fixed right-0 top-0 z-[100] flex h-full w-[86%] max-w-[360px] flex-col border-l border-[var(--foreground-bg-color)] bg-[var(--background-bg-color)] shadow-2xl transition-transform duration-200 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-[var(--foreground-bg-color)] px-4 py-3">
          {!safe && (
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                <FaSearch />
              </span>
              <input
                type="text"
                value={searchInput}
                placeholder={t('search')}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearch}
                className={`w-full rounded-full border border-gray-500 bg-[var(--middle-bg-color)] py-2.5 pl-10 text-white focus:border-white focus:outline-none ${
                  searchInput.trim() ? 'pr-12' : 'pr-3'
                }`}
              />
              {searchInput.trim() && (
                <button
                  type="button"
                  onClick={submitSearch}
                  aria-label={t('search')}
                  className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-[var(--main-bg-color)] text-white transition-colors hover:bg-[var(--secondary-bg-color)]"
                >
                  <FaSearch />
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('closeMenu')}
            className="ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--foreground-bg-color)] text-white transition-colors hover:bg-[var(--secondary-bg-color)]"
          >
            <FaTimes />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          {!safe &&
            (isPlusOrHigher ? (
              <Link
                href="/gems"
                onClick={onClose}
                className="mb-2 flex items-center gap-4 rounded-xl border border-white/20 bg-[var(--main-bg-color)] px-3 py-3 font-bold text-white transition-transform hover:scale-[1.01]"
              >
                <span className="flex w-5 justify-center text-[17px]">
                  <FaGem />
                </span>
                <span className="flex-1 text-[15px]">{t('getGems')}</span>
              </Link>
            ) : (
              <Link
                href="/upgrade"
                onClick={onClose}
                className="mb-2 flex items-center gap-4 rounded-xl border border-white/20 bg-[#F2AF50] px-3 py-3 font-bold text-[#1A0A00] transition-transform hover:scale-[1.01]"
              >
                <span className="flex w-5 justify-center text-[17px]">
                  <BsRocketTakeoffFill />
                </span>
                <span className="flex-1 text-[15px]">{t('upgrade')}</span>
              </Link>
            ))}

          {/* Explore / Create / Chat deliberately absent: this drawer only
              exists below md, and below md the header's second row carries
              those three permanently — the trigger for this drawer sits in the
              row directly above them. What's left is the account menu proper
              (balance, profile, settings) plus the secondary links that have
              nowhere else to live. */}

          {!safe && !isPlusOrHigher && (
            <Row
              icon={<FaGem />}
              label={t('earnGems')}
              href="/earn-gems"
              active={pathname.startsWith('/earn-gems')}
            />
          )}

          <Row
            icon={<FaHome />}
            label={t('profile')}
            href={`/profile/${user.uid}`}
            active={pathname.startsWith('/profile/')}
          />
          <Row
            icon={<FaBullhorn />}
            label={t('announcements')}
            href="/announcements"
            active={pathname.startsWith('/announcements')}
          />
          {staff && (
            <Row
              icon={<FaLayerGroup />}
              label={t('categoriesNav')}
              href="/categories"
              active={pathname.startsWith('/categories')}
            />
          )}

          <div className="my-2 h-px bg-[var(--foreground-bg-color)]" />

          {!safe && (
            <Row
              icon={<FaDiscord />}
              label="Discord"
              href="https://discord.gg/cZuJFFb34e"
              external
            />
          )}
          <Row
            icon={<HiMail />}
            label={t('contact')}
            href="/contact"
            active={pathname === '/contact'}
          />
          {!safe && (
            <Row
              icon={<FaBookOpen />}
              label={t('guide')}
              href="/articles"
              active={pathname.startsWith('/articles')}
            />
          )}
          {!safe && (
            <Row
              icon={<FaCode />}
              label={t('API')}
              href="/docs"
              active={pathname.startsWith('/docs')}
            />
          )}
          <Row
            // Gold matches the Pro tier's gradient mid-stop.
            icon={<FaCrown className="text-[#F2AF50]" />}
            label={t('affiliate')}
            href="https://aiffiliate.io"
            external
          />

          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            className="flex w-full items-center justify-center rounded-xl px-3 py-2 text-[#9A9AA8] transition-colors hover:bg-[var(--foreground-bg-color)]"
            aria-label={t('moreLinks')}
          >
            {moreOpen ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          {moreOpen && (
            <>
              <Row
                label={t('privacyPolicy')}
                href="/privacypolicy"
                active={pathname === '/privacypolicy'}
              />
              <Row
                label={t('termsOfService')}
                href="/termsofservice"
                external
              />
              <Row
                label={t('refundpolicy')}
                href="/refund"
                active={pathname === '/refund'}
              />
              <Row
                label={t('dmcapolicy')}
                href="/dmcapolicy"
                active={pathname === '/dmcapolicy'}
              />
              <Row
                label={t('moderationpolicy')}
                href="/contentmoderationpolicy"
                active={pathname.startsWith('/contentmoderationpolicy')}
              />
            </>
          )}
        </div>

        <div className="shrink-0 border-t border-[var(--foreground-bg-color)] p-3">
          <Link
            href="/settings"
            onClick={onClose}
            aria-label={t('accountSettings')}
            className="flex flex-col gap-2 rounded-xl p-2 transition-colors hover:bg-[var(--foreground-bg-color)]"
          >
            {/* Inside the card, not above it: this identity block is the menu's
                only route into /settings, and unlabelled it reads as "my
                profile" — you had to guess that your own name opens account
                settings. Part of the button so it highlights with it. */}
            <div
              className="flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-wider"
              style={{ color: SUBTLE }}
            >
              <FaCog size={11} />
              {t('accountSettings')}
            </div>

            <div className="flex items-center gap-3">
              {avatar}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-white">
                    {username || user.uid}
                  </span>
                  <span
                    className={`shrink-0 text-xs font-semibold ${planTextClass}`}
                  >
                    {planDisplay}
                  </span>
                </div>
                <div className="truncate text-xs" style={{ color: SUBTLE }}>
                  {email || user?.email || 'No email'}
                </div>
              </div>
              <FaChevronRight size={12} style={{ color: SUBTLE }} />
            </div>
          </Link>
        </div>
      </aside>
    </>
  )
}
